import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import translations
import commonEN from './locales/en/common.json';
import ordersEN from './locales/en/orders.json';
import returnsEN from './locales/en/returns.json';
import productsEN from './locales/en/products.json';
import cartEN from './locales/en/cart.json';
import checkoutEN from './locales/en/checkout.json';
import authEN from './locales/en/auth.json';
import userEN from './locales/en/user.json';
import dashboardEN from './locales/en/dashboard.json';
import userAdminEN from './locales/en/userAdmin.json';
import categoriesEN from './locales/en/categories.json';
import couponEN from './locales/en/coupon.json';
import miscEN from './locales/en/misc.json';
import shippingEN from './locales/en/shipping.json';
import hooksEN from './locales/en/hooks.json';

import commonDE from './locales/de/common.json';
import ordersDE from './locales/de/orders.json';
import returnsDE from './locales/de/returns.json';
import productsDE from './locales/de/products.json';
import cartDE from './locales/de/cart.json';
import checkoutDE from './locales/de/checkout.json';
import authDE from './locales/de/auth.json';
import userDE from './locales/de/user.json';
import dashboardDE from './locales/de/dashboard.json';
import userAdminDE from './locales/de/userAdmin.json';
import categoriesDE from './locales/de/categories.json';
import couponDE from './locales/de/coupon.json';
import miscDE from './locales/de/misc.json';
import shippingDE from './locales/de/shipping.json';
import hooksDE from './locales/de/hooks.json';

import commonFR from './locales/fr/common.json';
import ordersFR from './locales/fr/orders.json';
import returnsFR from './locales/fr/returns.json';
import productsFR from './locales/fr/products.json';
import cartFR from './locales/fr/cart.json';
import checkoutFR from './locales/fr/checkout.json';
import authFR from './locales/fr/auth.json';
import userFR from './locales/fr/user.json';
import dashboardFR from './locales/fr/dashboard.json';
import userAdminFR from './locales/fr/userAdmin.json';
import categoriesFR from './locales/fr/categories.json';
import couponFR from './locales/fr/coupon.json';
import miscFR from './locales/fr/misc.json';
import shippingFR from './locales/fr/shipping.json';
import hooksFR from './locales/fr/hooks.json';

import commonIT from './locales/it/common.json';
import ordersIT from './locales/it/orders.json';
import returnsIT from './locales/it/returns.json';
import productsIT from './locales/it/products.json';
import cartIT from './locales/it/cart.json';
import checkoutIT from './locales/it/checkout.json';
import authIT from './locales/it/auth.json';
import userIT from './locales/it/user.json';
import dashboardIT from './locales/it/dashboard.json';
import userAdminIT from './locales/it/userAdmin.json';
import categoriesIT from './locales/it/categories.json';
import couponIT from './locales/it/coupon.json';
import miscIT from './locales/it/misc.json';
import shippingIT from './locales/it/shipping.json';
import hooksIT from './locales/it/hooks.json';

// Resources object with translations and namespaces
const resources = {
  en: {
    common: commonEN,
    orders: ordersEN,
    returns: returnsEN,
    products: productsEN,
    cart: cartEN,
    checkout: checkoutEN,
    auth: authEN,
    user: userEN,
    dashboard: dashboardEN,
    userAdmin: userAdminEN,
    categories: categoriesEN,
    coupon: couponEN,
    misc: miscEN,
    shipping: shippingEN,
    hooks: hooksEN,
  },
  de: {
    common: commonDE,
    orders: ordersDE,
    returns: returnsDE,
    products: productsDE,
    cart: cartDE,
    checkout: checkoutDE,
    auth: authDE,
    user: userDE,
    dashboard: dashboardDE,
    userAdmin: userAdminDE,
    categories: categoriesDE,
    coupon: couponDE,
    misc: miscDE,
    shipping: shippingDE,
    hooks: hooksDE,
  },
  fr: {
    common: commonFR,
    orders: ordersFR,
    returns: returnsFR,
    products: productsFR,
    cart: cartFR,
    checkout: checkoutFR,
    auth: authFR,
    user: userFR,
    dashboard: dashboardFR,
    userAdmin: userAdminFR,
    categories: categoriesFR,
    coupon: couponFR,
    misc: miscFR,
    shipping: shippingFR,
    hooks: hooksFR,
  },
  it: {
    common: commonIT,
    orders: ordersIT,
    returns: returnsIT,
    products: productsIT,
    cart: cartIT,
    checkout: checkoutIT,
    auth: authIT,
    user: userIT,
    dashboard: dashboardIT,
    userAdmin: userAdminIT,
    categories: categoriesIT,
    misc: miscIT,
    coupon: couponIT,
    shipping: shippingIT,
    hooks: hooksIT,
  },
};

// Create a cache for missing keys to avoid repeated console logs
const missingKeyCache = new Set();

/**
 * Initialize i18next with configuration and namespace support
 */
i18n
  // Load translation using http -> see /public/locales
  .use(Backend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Init i18next
  .init({
    resources,
    lng: 'fr',
    fallbackLng: 'fr',
    debug: false, // Set to false to reduce console logs

    // Set default namespace
    defaultNS: 'common',

    // Configure namespaces
    ns: [
      'common',
      'orders',
      'returns',
      'products',
      'cart',
      'checkout',
      'auth',
      'user',
      'dashboard',
      'userAdmin',
      'categories',
      'coupon',
      'misc',
      'shipping',
      'hooks',
    ],

    // Handle missing keys
    saveMissing: false, // Don't save missing translations
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      // Only log missing keys in development mode and only once per key
      if (process.env.NODE_ENV === 'development' && !missingKeyCache.has(key)) {
        missingKeyCache.add(key);
        console.debug(`Missing translation key: ${key} in namespace: ${ns}`);
      }
    },

    // Return key if translation is missing
    returnNull: false,
    returnEmptyString: false,
    returnObjects: false,
    fallbackValue: (key) => key.split('.').pop(),

    interpolation: {
      escapeValue: false, // React already safes from XSS
    },

    // Detection options
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
